import { Layers3, PanelLeftClose, PanelLeftOpen } from "lucide-react";

import { useStylePanel } from "./style-panel-context";
import { LayerNavigator } from "./layer-navigator";

interface LayerNavigatorPanelProps {
  remoteEditorsByComponent?: ReadonlyMap<string, readonly { userId: string; displayName: string; color: string }[]>;
  isCollapsed?: boolean;
  onCollapseToggle?: () => void;
}

export function LayerNavigatorPanel({
  remoteEditorsByComponent,
  isCollapsed = false,
  onCollapseToggle,
}: LayerNavigatorPanelProps) {
  const {
    data,
    selectedComponentId,
    setSelectedComponentId,
    hiddenComponentIds,
    lockedComponentIds,
    toggleComponentHidden,
    toggleComponentLocked,
    reorderComponents,
  } = useStylePanel();

  if (isCollapsed) {
    return (
      <div className="flex h-full w-12 flex-col items-center border-r border-border bg-card py-3">
        <button
          type="button"
          onClick={onCollapseToggle}
          className="rounded-lg border border-border p-2 text-muted-foreground transition hover:bg-accent hover:text-foreground"
          title="Expand layers"
          aria-label="Expand layers"
        >
          <PanelLeftOpen className="h-4 w-4" />
        </button>
        <div className="mt-4 rounded-lg bg-[var(--color-primary)]/10 p-2 text-[var(--color-primary)]">
          <Layers3 className="h-4 w-4" />
        </div>
        <div className="mt-3 rotate-180 [writing-mode:vertical-rl] text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
          Layers
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col border-r border-border bg-card">
      <div className="flex items-center justify-between gap-2 border-b border-border px-4 py-3">
        <div className="flex min-w-0 items-center gap-2">
          <Layers3 className="h-4 w-4 shrink-0 text-[var(--color-primary)]" />
          <div className="min-w-0">
            <div className="text-sm font-semibold text-foreground">Layers</div>
            <div className="truncate text-[11px] text-muted-foreground">Select, reorder, hide, and lock blocks</div>
          </div>
        </div>
        <button
          type="button"
          onClick={onCollapseToggle}
          className="rounded-md p-1.5 text-muted-foreground transition hover:bg-accent hover:text-foreground"
          title="Collapse layers"
          aria-label="Collapse layers"
        >
          <PanelLeftClose className="h-4 w-4" />
        </button>
      </div>
      <div className="flex-1 overflow-y-auto p-3">
        <LayerNavigator
          data={data}
          selectedId={selectedComponentId}
          hiddenIds={hiddenComponentIds}
          lockedIds={lockedComponentIds}
          remoteEditorsByComponent={remoteEditorsByComponent}
          onSelect={setSelectedComponentId}
          onReorder={reorderComponents}
          onVisibilityToggle={toggleComponentHidden}
          onLockToggle={toggleComponentLocked}
        />
      </div>
    </div>
  );
}
